import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { waeAPI, STATUS_STYLE, STATUS_LABEL, PRIORITY_STYLE, fmtDT } from "./waeApi";
import { Inbox, Search, AlertTriangle, Clock } from "lucide-react";
import RequestDrawer from "./RequestDrawer";
import { EmptyState } from "./uiKit";

const DUE_TABS = [
  { k: "",          label: "All" },
  { k: "today",     label: "Due Today" },
  { k: "overdue",   label: "Overdue" },
];

const PRIORITY_TABS = [
  { k: "",       label: "Any priority" },
  { k: "urgent", label: "Urgent" },
  { k: "high",   label: "High" },
];

export default function WAEInbox() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("");
  const [openId, setOpenId] = useState(null);
  const [types, setTypes] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");

  const load = () => waeAPI.inbox({
    priority: priority || undefined,
    approval_type: typeFilter || undefined,
    due: due || undefined,
  }).then(setRows).catch(()=>{});
  useEffect(() => { load(); }, [due, priority, typeFilter]);   // eslint-disable-line
  useEffect(() => { waeAPI.approvalTypes().then(setTypes); }, []);

  const filtered = useMemo(() => {
    if (!q) return rows;
    return rows.filter(r =>
      r.code.toLowerCase().includes(q.toLowerCase()) ||
      r.subject.toLowerCase().includes(q.toLowerCase()) ||
      (r.requester_email || "").toLowerCase().includes(q.toLowerCase())
    );
  }, [rows, q]);

  const overdue = filtered.filter(r => r.overdue).length;

  return (
    <div className="space-y-4" data-testid="wae-inbox">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search inbox…" aria-label="Search inbox"
                 data-testid="wae-inbox-search"
                 className="h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13px] w-72 focus:border-hocp-navy outline-none"/>
        </div>
        <div className="flex gap-1" data-testid="wae-inbox-due">
          {DUE_TABS.map(t => (
            <button key={t.k} onClick={()=>setDue(t.k)}
                    data-testid={`wae-inbox-due-${t.k || "all"}`}
                    className={`h-9 px-3 rounded-lg text-[12.5px] font-medium border ${
                      due===t.k ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {t.label}{t.k === "overdue" && overdue ? ` (${overdue})` : ""}
            </button>
          ))}
        </div>
        <select value={priority} onChange={e=>setPriority(e.target.value)}
                data-testid="wae-inbox-priority"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          {PRIORITY_TABS.map(t => <option key={t.k} value={t.k}>{t.label}</option>)}
        </select>
        <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}
                data-testid="wae-inbox-type"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">All approval types</option>
          {types.filter(t=>t.is_active).map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
        </select>
        <div className="ml-auto text-[12px] text-slate-500 mono">
          {filtered.length} pending · {overdue > 0 && <span className="text-rose-600 font-medium">{overdue} overdue</span>}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <EmptyState icon={Inbox} title="Inbox Zero"
            msg="No approvals waiting for your action right now."/>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Subject</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Requester</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-center px-4 py-3">Priority</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">SLA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(r => (
                <tr key={r.id} onClick={()=>setOpenId(r.id)}
                    data-testid={`wae-inbox-row-${r.id}`}
                    className={`cursor-pointer hover:bg-slate-50/70 ${r.overdue ? "bg-rose-50/30" : ""}`}>
                  <td className="px-4 py-3 mono font-medium text-hocp-navy">{r.code}</td>
                  <td className="px-4 py-3 max-w-xs truncate">
                    <div className="font-medium text-hocp-navy truncate">{r.subject}</div>
                    <div className="text-[11px] text-slate-400 mono truncate">{r.template_name}</div>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-slate-600">{r.approval_type_name}</td>
                  <td className="px-4 py-3 text-[12px] text-slate-600 truncate max-w-[160px]">{r.requester_name || r.requester_email}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">
                    {r.amount != null ? `${r.currency||"INR"} ${Number(r.amount).toLocaleString("en-IN")}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${PRIORITY_STYLE[r.priority]}`}>{r.priority}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium border ${STATUS_STYLE[r.status] || ""}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[12px] mono">
                    {r.overdue
                      ? <span className="text-rose-600 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3"/>Overdue</span>
                      : <span className="text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{fmtDT(r.sla_due_at)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId && <RequestDrawer requestId={openId} currentUserId={user?.id || user?._id}
                                  onClose={()=>setOpenId(null)} onChanged={load}/>}
    </div>
  );
}
